import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function formatNumber(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "0";
  return new Intl.NumberFormat("en-US").format(n);
}

export function initials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export function podImageUrl(filename) {
  if (!filename) return null;
  // New driver-app uploads are Object Storage paths (always contain "/",
  // e.g. "pod/89079/<uuid>.jpeg"); legacy PHP-uploaded photos are bare
  // filenames served from the old local static mount.
  if (filename.includes("/")) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/v2/driver/pod-photos/${filename}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}/api/v2/uploads/pod/${filename}`;
}

let _businessTz = "America/Edmonton";

// Called once at app boot (see AuthContext) after fetching the persisted
// Settings > Business Timezone value — kept as a plain module variable
// (not React state) so todayLocal() below can stay a synchronous function
// that every page already calls directly inside useState()/render.
export function setBusinessTimezone(tz) {
  if (tz) _businessTz = tz;
}

export function todayLocal() {
  // Always resolve "today" in the OUTLET's own timezone (configurable via
  // Settings > Business Timezone, defaults to Edmonton), not the viewer's
  // device timezone — dispatch/route dates are anchored to the business, not
  // whoever happens to be looking at the dashboard right now. NEVER use
  // new Date().toISOString().slice(0,10) here: that renders in UTC and
  // silently shows tomorrow's date for several hours every evening.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: _businessTz, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatDate(value) {
  if (!value) return "—";
  // Plain calendar dates ("2026-08-18", no time-of-day) must NEVER go through
  // timezone conversion — `new Date("2026-08-18")` is parsed as UTC midnight,
  // then rendered in the VIEWER's local timezone, which silently rolls the
  // day back by one for anyone west of UTC (e.g. Edmonton). Read the Y-M-D
  // digits directly and print them as-is instead.
  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (isoDateOnly) {
    const [, y, m, d] = isoDateOnly;
    return `${MONTH_ABBR[Number(m) - 1]} ${Number(d)}, ${y}`;
  }
  const parsed = new Date(String(value).replace(/\//g, "-"));
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" }).format(parsed);
}

export function formatEpoch(value) {
  if (!value) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return formatDate(value);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  }).format(new Date(n * 1000));
}

export function formatDistanceKm(meters) {
  const n = Number(meters);
  if (!meters || Number.isNaN(n)) return "—";
  return `${(n / 1000).toFixed(1)} km`;
}

export function formatDurationHm(seconds) {
  const n = Number(seconds);
  if (!seconds || Number.isNaN(n)) return "—";
  const totalMinutes = Math.round(n / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}
